import React, { useState, useEffect, useRef } from "react";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import AgentAvatar from "./AgentAvatar";
import { modelSettingOf, modelShortLabel } from "../../../utils/agentModel";

// agent-experience-redesign-v1.md §4.2 (D3) — floating glass card
// shown after a 600ms hover delay (or 400ms long-press on touch).
// Cancels on scroll or pointer-leave. Z-index above all tiles but
// below modals.
//
// Content: large IconTile + name + description + meta chips
// (expert count, channels, status) + two CTAs (Open / Test in chat).
//
// Position: anchored to the source tile's bounding rect. Renders
// above by default; flips below if the tile is in the top third of
// the viewport.

const HOVER_DELAY_MS = 600;
const LONGPRESS_DELAY_MS = 400;
// Time to give the user to move their pointer from the tile onto the
// hover card. Without this, the card disappeared the instant the mouse
// left the tile (gap between tile and card is ~12px) and Open / Test
// buttons couldn't be clicked.
const HOVER_LEAVE_GRACE_MS = 200;

export default function TileHoverPreview({
  agent, anchorRect,
  onClose, onOpen, onTest,
  onCardEnter, onCardLeave,
  openLabel = "Open",
  testLabel = "Test in chat",
}) {
  if (!agent || !anchorRect) return null;

  // Position the card near the anchor: above if the anchor is in
  // the bottom 2/3 of the viewport; below otherwise.
  const cardW = 280;
  const cardH = 220;
  const margin = 12;
  const flipBelow = anchorRect.top < window.innerHeight / 3;
  const top = flipBelow
    ? anchorRect.bottom + margin
    : Math.max(margin, anchorRect.top - cardH - margin);
  const left = Math.min(
    Math.max(margin, anchorRect.left + anchorRect.width / 2 - cardW / 2),
    window.innerWidth - cardW - margin,
  );

  const description = agent.metadata_json?.description || agent.description;
  const channels = agent.channels || [];
  const expertCount = agent.flow_config?.supervisor?.experts?.length || 0;
  const isSupervisor = (agent.agent_kind || "regular") === "supervisor";

  return (
    <div
      onMouseEnter={onCardEnter}
      onMouseLeave={onCardLeave || onClose}
      style={{
      position: "fixed", top, left, width: cardW,
      zIndex: 150,
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 14,
      padding: 14,
      boxShadow: MATERIALS?.spatialShadow ? MATERIALS.spatialShadow() : "0 12px 36px rgba(0,0,0,0.5)",
      display: "flex", flexDirection: "column", gap: 10,
      pointerEvents: "auto",
    }}>
      {/* Header — AgentAvatar + name + status */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <AgentAvatar agent={agent} size={42} variant="tile" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, lineHeight: 1.2,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {agent.name}
          </div>
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2,
                        display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: agent.status === "live" ? COLORS.green
                : agent.status === "paused" ? COLORS.orange
                : COLORS.textDim,
            }} />
            <span style={{ textTransform: "capitalize" }}>{agent.status || "draft"}</span>
            {agent.version && <span>· v{String(agent.version).replace(/^v+/, "")}</span>}
          </div>
        </div>
      </div>

      {/* Description */}
      {description && (
        <div style={{
          fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5,
          maxHeight: 64, overflow: "hidden",
          display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical",
        }}>{description}</div>
      )}

      {/* Meta chips */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {isSupervisor && (
          <Chip label={`${expertCount} expert${expertCount === 1 ? "" : "s"}`} />
        )}
        <Chip label={modelShortLabel(modelSettingOf(agent))} mono />
        {channels.length > 0 && (
          <Chip label={channels.slice(0, 3).join(" · ") + (channels.length > 3 ? ` +${channels.length - 3}` : "")} />
        )}
      </div>

      {/* CTAs */}
      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        <button type="button" onClick={(e) => { e.stopPropagation(); onOpen && onOpen(agent); }}
          style={{
            flex: 1, padding: "6px 10px", borderRadius: 6,
            background: COLORS.accent, color: "#fff", border: "none",
            fontSize: 11, fontWeight: 600, cursor: "pointer",
          }}>{openLabel}</button>
        {onTest && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onTest(agent); }}
            style={{
              flex: 1, padding: "6px 10px", borderRadius: 6,
              background: "transparent", color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}>{testLabel}</button>
        )}
      </div>
    </div>
  );
}

function Chip({ label, mono }) {
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 999,
      background: COLORS.surface, color: COLORS.textMuted,
      border: `1px solid ${COLORS.border}`,
      fontSize: 10, fontFamily: mono ? "monospace" : "inherit",
      whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

// Hook that wires hover-delay + long-press detection to a tile.
// Returns { onMouseEnter, onMouseLeave, onTouchStart, onTouchEnd,
//           onTouchMove, onScroll } — spread on the tile.
// Calls onTrigger(rect) when the delay completes; onCancel when the
// pointer leaves or the user scrolls.
export function useHoverTrigger({ onTrigger, onCancel }) {
  const timerRef = useRef(null);
  const elRef = useRef(null);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      onCancel && onCancel();
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [onCancel]);

  const start = (delay, target) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    elRef.current = target;
    timerRef.current = setTimeout(() => {
      const rect = target.getBoundingClientRect();
      onTrigger && onTrigger(rect);
    }, delay);
  };
  const cancel = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return {
    onMouseEnter: (e) => start(HOVER_DELAY_MS, e.currentTarget),
    onMouseLeave: () => { cancel(); onCancel && onCancel(); },
    onTouchStart: (e) => start(LONGPRESS_DELAY_MS, e.currentTarget),
    onTouchEnd: () => { cancel(); onCancel && onCancel(); },
    onTouchMove: () => { cancel(); onCancel && onCancel(); },
  };
}
